import { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "@localization/useTranslation";
import { useFilterState } from "@common/hooks/useFilterState";
import { useStateSessionStorage } from "@common/hooks/useStateSessionStorage";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching";
import { FilterOptionType } from "@common/components/filterPopover/FilterPopover.types";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useUrlProvider } from "../../../useUrlProvider";
import { InternalAssessmentGroupDraft } from "../../InternalAssessmentGroups.types";
import { assigneeFilterCandidates, toDistinctOptions } from "../../InternalAssessmentGroups.utils";
import { AssigneeType, SelectableAssignee } from "../../SelectableAssignee.types";
import { getAssigneeTypeName, getAssigneeTypeTooltip } from "../../SelectableAssignee.utils";
import { IInternalAssessmentGroupAssigneeSelectorProps } from "./InternalAssessmentGroupAssigneeSelector";

export default function useInternalAssessmentGroupAssigneeSelector(props: IInternalAssessmentGroupAssigneeSelectorProps) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const navigate = useNavigate();
    const { getInternalAssessmentGroupUrl } = useUrlProvider();
    const [showSendDialog, setShowSendDialog] = useState(false);
    const [showAddRecipientDialog, setShowAddRecipientDialog] = useState(false);

    const { selectFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState("assessment-group-recipients");
    const [searchString, setSearchString] = useStateSessionStorage<string>("assessment-group-recipients-search", "");

    const businessAreaIds = (selectFilters.businessAreas ?? []).filter((id) =>
        props.rows.some((row) => row.businessAreas.some((businessArea) => businessArea.id === id))
    );
    const recipientTypes = selectFilters.recipientTypes ?? [];

    const rows = applyClientFilters(
        props.rows,
        assigneeFilterCandidates,
        { ...selectFilters, businessAreas: businessAreaIds, recipientTypes },
        {},
        filterOperators
    )!;

    const selectedAssigneeIds = new Set(props.selectedAssignees.map((assignee) => assignee.assigneeId));

    const isSelected = (row: SelectableAssignee) => selectedAssigneeIds.has(row.id);

    const onCheckBoxClick = (row: SelectableAssignee) => {
        if (isSelected(row)) {
            props.onSelectedAssigneesChange(props.selectedAssignees.filter((assignee) => assignee.assigneeId !== row.id));
            return;
        }

        props.onSelectedAssigneesChange([...props.selectedAssignees, { assigneeId: row.id, assigneeType: row.assigneeType }]);
    };

    // Ticking hands over the rows the table currently shows, which narrows further by the search string, so
    // selecting all while a search is active must not reach the recipients the search hid. Unticking hands over
    // a bare empty array instead, naming no rows at all, so that direction falls back to `rows` - the same list
    // `headerCheckBoxState` is derived from, which is what made the box appear ticked in the first place.
    const onSelectAll = (visibleRows: Array<SelectableAssignee>) => {
        if (visibleRows.length === 0) {
            const idsToDeselect = new Set(rows.map((row) => row.id));
            props.onSelectedAssigneesChange(props.selectedAssignees.filter((assignee) => !idsToDeselect.has(assignee.assigneeId)));
            return;
        }

        const toAdd: Array<InternalAssessmentGroupDraft> = visibleRows
            .filter((row) => !isSelected(row))
            .map((row) => ({ assigneeId: row.id, assigneeType: row.assigneeType }));

        props.onSelectedAssigneesChange([...props.selectedAssignees, ...toAdd]);
    };

    const numberOfSelectedRows = rows.filter(isSelected).length;
    const headerCheckBoxState: "true" | "false" | "indeterminate" =
        numberOfSelectedRows === 0 ? "false" : numberOfSelectedRows === rows.length ? "true" : "indeterminate";

    // Only the business areas actually held by a selectable recipient, so the filter never offers a value that
    // can match nothing.
    const businessAreaOptions = toDistinctOptions(props.rows.flatMap((row) => row.businessAreas).map(({ id, name }) => ({ id, name })));

    const getFilterDataOptions = () =>
        withOperators(
            [
                {
                    type: FilterOptionType.SelectField,
                    id: "businessAreas",
                    value: businessAreaIds,
                    label: translateString("businessAreas"),
                    fixedData: businessAreaOptions,
                },
                {
                    type: FilterOptionType.SelectField,
                    id: "recipientTypes",
                    value: recipientTypes,
                    label: translateString("recipientType"),
                    fixedData: [AssigneeType.LegalEntityContact, AssigneeType.User, AssigneeType.ExternalUser].map((assigneeType) => ({
                        id: assigneeType.toString(),
                        name: getAssigneeTypeName(assigneeType, translateString),
                        tooltip: getAssigneeTypeTooltip(assigneeType, translateString),
                    })),
                },
            ],
            filterOperators
        );

    const selectedAssigneeRows = props.rows.filter(isSelected);

    // A recipient is created in order to be sent to, so it arrives already ticked. The draft is saved before the
    // page reloads its rows, so the reload answers with a list that already holds both the recipient and its draft.
    const onRecipientCreated = async (externalUserId: string) => {
        setShowAddRecipientDialog(false);

        await props.onSelectedAssigneesChange([...props.selectedAssignees, { assigneeId: externalUserId, assigneeType: AssigneeType.ExternalUser }]);
        await props.onRecipientAdded();
    };

    const onCancelClick = () => navigate(getInternalAssessmentGroupUrl(props.internalAssessmentGroupId));

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        rows,
        isSelected,
        onCheckBoxClick,
        onSelectAll,
        headerCheckBoxState,
        getFilterDataOptions,
        onFilterChange,
        searchString,
        setSearchString,
        selectedAssigneeRows,
        showSendDialog,
        setShowSendDialog,
        // Creating a recipient is a user-management action, so without that permission the page offers only the
        // recipients that already exist.
        canCreateRecipients: permissions.canManageUsers,
        showAddRecipientDialog,
        setShowAddRecipientDialog,
        onRecipientCreated,
        onCancelClick,
    };
}
